import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Sprout, Leaf, ChevronRight, Trash2, Edit2, X, Image } from 'lucide-react';
import DatePicker from '../components/DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { active: 'Aktiv', completed: 'Abgeschlossen', paused: 'Pausiert' };
const STATUS_COLORS = { active: 'badge-green', completed: 'badge-blue', paused: 'badge-yellow' };

const DEFAULT_COVER = 'https://images.unsplash.com/photo-1707055039188-b95a442223dd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', grow_group_id: '', start_date: '', cover_image: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, gRes] = await Promise.all([
        axios.get(`${API}/api/projects`),
        axios.get(`${API}/api/groups`),
      ]);
      setProjects(Array.isArray(pRes.data) ? pRes.data : []);
      setGroups(Array.isArray(gRes.data) ? gRes.data : []);
    } catch { setProjects([]); setGroups([]); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditProject(null);
    setForm({ name: '', description: '', grow_group_id: '', start_date: '', cover_image: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProject(p);
    setForm({ name: p.name, description: p.description || '', grow_group_id: p.grow_group_id || '', start_date: p.start_date || '', cover_image: p.cover_image || '' });
    setError('');
    setShowModal(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, cover_image: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name ist erforderlich'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, grow_group_id: form.grow_group_id || null, start_date: form.start_date || null };
      if (editProject) {
        const { data } = await axios.put(`${API}/api/projects/${editProject.id}`, payload);
        setProjects(prev => prev.map(p => p.id === data.id ? data : p));
      } else {
        const { data } = await axios.post(`${API}/api/projects`, payload);
        setProjects(prev => [data, ...prev]);
      }
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Projekt wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/api/projects/${id}`);
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Grow-Projekte</h1>
          <p className="text-emerald-100/50 text-sm mt-1">{projects.length} Projekt(e)</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-project-btn">
          <Plus size={16} /><span>Neu</span>
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16" data-testid="no-projects">
          <Sprout size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-4">Noch keine Projekte. Lege dein erstes an!</p>
          <button onClick={openCreate} className="btn-primary">Projekt erstellen</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map(p => (
            <div key={p.id} className="card-grow overflow-hidden" data-testid={`project-card-${p.id}`}>
              <div className="h-32 relative overflow-hidden">
                <img src={p.cover_image || DEFAULT_COVER} alt={p.name}
                  className="w-full h-full object-cover"
                  onError={e => { e.target.src = DEFAULT_COVER; }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,10,7,0.8) 0%, transparent 60%)' }} />
                <div className="absolute top-2 right-2 flex gap-1">
                  <button onClick={(e) => { e.preventDefault(); openEdit(p); }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white/70 hover:text-white transition-colors"
                    style={{ background: 'rgba(0,0,0,0.5)' }} data-testid={`edit-project-${p.id}`}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={(e) => { e.preventDefault(); handleDelete(p.id); }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white/70 hover:text-red-400 transition-colors"
                    style={{ background: 'rgba(0,0,0,0.5)' }} data-testid={`delete-project-${p.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="absolute bottom-2 left-3">
                  <span className={`badge ${STATUS_COLORS[p.status] || 'badge-green'}`}>{STATUS_LABELS[p.status] || p.status}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-heading font-semibold text-emerald-50 truncate">{p.name}</h3>
                {p.description && <p className="text-sm text-emerald-100/50 mt-1 line-clamp-2">{p.description}</p>}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1 text-xs text-emerald-100/40">
                    <Leaf size={12} />
                    <span>{p.plant_count || 0} Pflanze(n)</span>
                  </div>
                  {p.start_date && <span className="text-xs text-emerald-100/30">Start: {new Date(p.start_date).toLocaleDateString('de-DE')}</span>}
                </div>
                <Link to={`/projects/${p.id}`} className="flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300 mt-3 transition-colors">
                  Details ansehen <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" data-testid="project-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-semibold text-emerald-50 text-lg">
                {editProject ? 'Projekt bearbeiten' : 'Neues Projekt'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100 transition-colors">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Name *</label>
                <input className="input-grow" placeholder="z.B. Indoor Grow #1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="project-name-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Beschreibung</label>
                <textarea className="input-grow" rows={2} placeholder="Optionale Beschreibung" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="project-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Gruppe</label>
                  <select className="input-grow" value={form.grow_group_id} onChange={e => setForm(f => ({ ...f, grow_group_id: e.target.value }))} data-testid="project-group-select">
                    <option value="">Keine</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Startdatum</label>
                  <DatePicker
                    value={form.start_date}
                    onChange={v => setForm(f => ({ ...f, start_date: v }))}
                    placeholder="Startdatum wählen"
                    data-testid="project-start-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Titelbild</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                    📷 Foto aufnehmen
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                  </label>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                    🖼️ Aus Galerie
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
                {form.cover_image && (
                  <img src={form.cover_image} alt="Vorschau" className="mt-2 w-full h-20 object-cover rounded-lg" />
                )}
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1" data-testid="cancel-project-btn">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-project-btn">
                  {saving ? 'Speichern...' : editProject ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
